import React, { useState, useEffect } from 'react';
import { attendanceApi, LeaveRequest, LeaveCancelRequest } from '../services/attendanceApi';
import { useAlert } from '../contexts/AlertContext';
import * as XLSX from 'xlsx';

interface PendingRequest {
  id: number;
  applicant: string;
  type: string;
  period: string;
  reason: string;
  requestType: 'leave' | 'cancel'; // 휴가 신청인지 취소 요청인지 구분
}

interface EmployeeLeaveBalance {
  id: number;
  name: string;
  joinDate: Date;
  totalLeave: number;
  usedLeave: number;
  remainingLeave: number;
}

interface ConfirmationModalProps {
  isOpen: boolean;
  request: PendingRequest | null;
  isApproval: boolean;
  onClose: () => void;
  onConfirm: (requestId: number, isApproved: boolean, rejectReason?: string) => void;
}

interface UsedLeaveModalProps {
  isOpen: boolean;
  employeeName: string;
  usedLeaves: LeaveRequest[];
  onClose: () => void;
}

const UsedLeaveModal: React.FC<UsedLeaveModalProps> = ({ isOpen, employeeName, usedLeaves, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose}></div>
      <div className="w-full max-w-3xl bg-white rounded-xl shadow-2xl relative z-10 max-h-[80vh] flex flex-col">
        <header className="p-6 border-b border-slate-200">
          <h3 className="font-bold text-xl">{employeeName}님의 사용 연차 내역</h3>
          <p className="text-sm text-slate-500 mt-1">총 {usedLeaves.length}건</p>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          {usedLeaves.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <p>사용한 연차가 없습니다.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {usedLeaves.map((leave, index) => (
                <div
                  key={leave.id}
                  className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded">
                          {leave.type}
                        </span>
                        <span className="text-sm font-semibold text-slate-700">
                          {new Date(leave.startDate).toLocaleDateString('ko-KR')} ~ {new Date(leave.endDate).toLocaleDateString('ko-KR')}
                        </span>
                        <span className="text-sm font-bold text-indigo-600">
                          {leave.days}일
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-2">
                        <span className="font-medium">사유:</span> {leave.reason}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        신청일: {new Date(leave.requestDate).toLocaleDateString('ko-KR')} |
                        승인자: {leave.approver || '-'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <footer className="p-6 bg-slate-50 border-t border-slate-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            닫기
          </button>
        </footer>
      </div>
    </div>
  );
};

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  request,
  isApproval,
  onClose,
  onConfirm
}) => {
  const [rejectReason, setRejectReason] = useState('');

  // 모달이 닫힐 때 입력값 초기화
  useEffect(() => {
    if (!isOpen) {
      setRejectReason('');
    }
  }, [isOpen]);

  if (!isOpen || !request) return null;

  const action = isApproval ? '승인' : '반려';

  const handleConfirm = () => {
    onConfirm(request.id, isApproval, rejectReason || undefined);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose}></div>
      <div
        className={`w-full max-w-md bg-white rounded-xl shadow-2xl relative z-10 transform transition-all duration-300 ${
          isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
      >
        <div className="p-6">
          <h3 className="font-bold text-lg">신청 {action} 확인</h3>
          <div className="mt-2 p-3 bg-slate-50 rounded-md text-sm">
            <p><span className="font-semibold">신청자:</span> {request.applicant}</p>
            <p><span className="font-semibold">기간:</span> {request.period}</p>
          </div>
          <p className="mt-4 text-sm text-slate-600">이 요청을 처리하시겠습니까?</p>
          {!isApproval && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                반려 사유 <span className="text-slate-400 font-normal">(선택)</span>
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="반려 사유를 입력하세요"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none"
                rows={3}
              />
            </div>
          )}
        </div>
        <footer className="p-4 bg-slate-50 flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            취소
          </button>
          <button
            onClick={handleConfirm}
            className={`px-4 py-2 text-sm font-semibold text-white rounded-lg ${
              isApproval
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-rose-600 hover:bg-rose-700'
            }`}
          >
            {action}
          </button>
        </footer>
      </div>
    </div>
  );
};

const AttendanceAdmin: React.FC = () => {
  const { showToast, showConfirm } = useAlert();
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [approvedRequests, setApprovedRequests] = useState<PendingRequest[]>([]);
  const [approvedLeaveRaw, setApprovedLeaveRaw] = useState<LeaveRequest[]>([]);
  const [leaveBalances, setLeaveBalances] = useState<EmployeeLeaveBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<PendingRequest | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isApproval, setIsApproval] = useState(false);

  // 사용 연차 모달 상태
  const [isUsedLeaveModalOpen, setIsUsedLeaveModalOpen] = useState(false);
  const [selectedEmployeeName, setSelectedEmployeeName] = useState<string>('');
  const [selectedEmployeeUsedLeaves, setSelectedEmployeeUsedLeaves] = useState<LeaveRequest[]>([]);

  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // 연도 필터 상태
  const [selectedYear, setSelectedYear] = useState<string>('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // 대기중인 휴가 신청 조회
      const pendingLeaveRequests = await attendanceApi.getLeaveRequestsByStatus('상신중');
      const pendingLeaveData = pendingLeaveRequests.map(req => ({
        id: req.id,
        applicant: req.employee?.name || '알 수 없음',
        type: req.type,
        period: `${new Date(req.startDate).toLocaleDateString('ko-KR')} ~ ${new Date(req.endDate).toLocaleDateString('ko-KR')} (${req.days}일)`,
        reason: req.reason,
        requestType: 'leave' as const
      }));

      // 대기중인 취소 요청 조회
      const pendingCancelRequests = await attendanceApi.getCancelRequestsByStatus('취소 요청 중');
      const pendingCancelData = pendingCancelRequests.map(req => ({
        id: req.id,
        applicant: req.employee?.name || '알 수 없음',
        type: `${req.leaveRequest.type} 취소 요청`,
        period: `${new Date(req.leaveRequest.startDate).toLocaleDateString('ko-KR')} ~ ${new Date(req.leaveRequest.endDate).toLocaleDateString('ko-KR')} (${req.leaveRequest.days}일)`,
        reason: req.cancelReason,
        requestType: 'cancel' as const
      }));

      // 두 배열을 합치기
      setPendingRequests([...pendingLeaveData, ...pendingCancelData]);
      
      // 승인된 휴가 신청 조회
      const approvedLeaveRequests = await attendanceApi.getLeaveRequestsByStatus('승인');
      const approvedData = approvedLeaveRequests.map(req => ({
        id: req.id,
        applicant: req.employee?.name || '알 수 없음',
        type: req.type,
        period: `${new Date(req.startDate).toLocaleDateString('ko-KR')} ~ ${new Date(req.endDate).toLocaleDateString('ko-KR')} (${req.days}일)`,
        reason: req.reason,
        requestType: 'leave' as const
      }));
      setApprovedRequests(approvedData);
      setApprovedLeaveRaw(approvedLeaveRequests);

      // 전체 직원의 휴가 잔액 일괄 조회 (1회 API 호출)
      const allBalances = await attendanceApi.getAllLeaveBalances();
      setLeaveBalances(allBalances.map(b => ({
        id: b.employee?.id || 0,
        name: b.employeeName,
        joinDate: new Date(b.joinDate),
        totalLeave: typeof b.total === 'string' ? parseFloat(b.total) || 0 : (b.total ?? 0),
        usedLeave: typeof b.used === 'string' ? parseFloat(b.used) || 0 : (b.used ?? 0),
        remainingLeave: typeof b.remaining === 'string' ? parseFloat(b.remaining) || 0 : (b.remaining ?? 0),
      })));
      
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const openConfirmationModal = (request: PendingRequest, isApproved: boolean) => {
    setSelectedRequest(request);
    setIsApproval(isApproved);
    setIsModalOpen(true);
  };

  const closeConfirmationModal = () => {
    setSelectedRequest(null);
    setIsModalOpen(false);
  };

  const handleSubmitApproval = async (requestId: number, isApproved: boolean, rejectReason?: string) => {
    try {
      const action = isApproved ? '승인' : '반려';
      const request = pendingRequests.find(r => r.id === requestId);

      if (!request) {
        showToast('요청을 찾을 수 없습니다.', 'error');
        return;
      }

      if (request.requestType === 'cancel') {
        // 취소 요청 처리
        if (isApproved) {
          await attendanceApi.approveCancelRequest(requestId, '관리자');
        } else {
          await attendanceApi.rejectCancelRequest(requestId, rejectReason || '');
        }
      } else {
        // 휴가 신청 처리
        if (isApproved) {
          await attendanceApi.approveLeaveRequest(requestId, '관리자');
        } else {
          await attendanceApi.rejectLeaveRequest(requestId, rejectReason || '');
        }
      }
      
      // 데이터 새로고침
      await fetchData();

      showToast(`${action} 처리되었습니다.`, 'success');
    } catch (error: any) {

      // 상세한 오류 메시지 표시
      const errorMessage = error?.response?.data?.message || error?.message || '알 수 없는 오류가 발생했습니다.';
      const action = isApproved ? '승인' : '반려';

      showToast(`${action} 처리 중 오류가 발생했습니다: ${errorMessage}`, 'error');
      
      // 오류 발생 시도 데이터 새로고침 (상태 동기화)
      try {
        await fetchData();
      } catch (fetchError) {
      }
    }
    
    closeConfirmationModal();
  };
  
  const handleCancelApproval = async (requestId: number) => {
    const confirmed = await showConfirm('승인을 취소하시겠습니까? 상태가 대기중으로 변경됩니다.');
    if (!confirmed) {
      return;
    }

    try {
      // 상태를 다시 PENDING으로 변경
      await attendanceApi.updateLeaveRequest(requestId, {
        status: '상신중',
        approver: undefined
      });

      // 데이터 새로고침
      await fetchData();
      showToast('승인이 취소되었습니다.', 'success');
    } catch (error: any) {

      // 상세한 오류 메시지 표시
      const errorMessage = error?.response?.data?.message || error?.message || '알 수 없는 오류가 발생했습니다.';
      showToast(`승인 취소 중 오류가 발생했습니다: ${errorMessage}`, 'error');

      // 오류 발생 시도 데이터 새로고침
      try {
        await fetchData();
      } catch (fetchError) {
      }
    }
  };

  // 연도 목록 추출 (기간의 첫번째 날짜 기준)
  const availableYears = React.useMemo(() => {
    const years = new Set<number>();
    approvedRequests.forEach((request) => {
      // period 형식: "2024. 1. 1. ~ 2024. 1. 2. (1일)"
      const dateMatch = request.period.match(/(\d{4})\.\s*\d+\.\s*\d+\./);
      if (dateMatch) {
        years.add(parseInt(dateMatch[1]));
      }
    });
    return Array.from(years).sort((a, b) => b - a); // 최신 연도부터
  }, [approvedRequests]);

  // 연도별 필터링
  const filteredByYear = React.useMemo(() => {
    if (selectedYear === 'all') {
      return approvedRequests;
    }
    return approvedRequests.filter((request) => {
      const dateMatch = request.period.match(/(\d{4})\.\s*\d+\.\s*\d+\./);
      if (dateMatch) {
        return dateMatch[1] === selectedYear;
      }
      return false;
    });
  }, [approvedRequests, selectedYear]);

  // 페이지네이션 계산 (필터링된 데이터 기준)
  const totalPages = Math.ceil(filteredByYear.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentApprovedRequests = filteredByYear.slice(startIndex, endIndex);

  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 연도 필터 변경 핸들러
  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    setCurrentPage(1); // 필터 변경 시 첫 페이지로 이동
  };

  // 사용 연차 클릭 핸들러
  const handleUsedLeaveClick = async (employeeId: number, employeeName: string) => {
    try {
      // 해당 직원의 휴가 요청만 조회 후 승인 상태 필터
      const employeeRequests = await attendanceApi.getLeaveRequestsByEmployee(employeeId);
      const employeeLeaves = employeeRequests.filter(req => req.status === '승인');

      setSelectedEmployeeName(employeeName);
      setSelectedEmployeeUsedLeaves(employeeLeaves);
      setIsUsedLeaveModalOpen(true);
    } catch (error) {
      showToast('사용 연차 정보를 불러오는데 실패했습니다.', 'error');
    }
  };

  const closeUsedLeaveModal = () => {
    setIsUsedLeaveModalOpen(false);
    setSelectedEmployeeName('');
    setSelectedEmployeeUsedLeaves([]);
  };

  // 승인된 요청 엑셀 다운로드
  const handleExportToExcel = async () => {
    try {
      // 이미 로드된 승인 원본 데이터 활용 (API 재호출 불필요)
      const filteredRequests = selectedYear === 'all'
        ? approvedLeaveRaw
        : approvedLeaveRaw.filter(req => {
            const startYear = new Date(req.startDate).getFullYear();
            return startYear === parseInt(selectedYear);
          });

      if (filteredRequests.length === 0) {
        showToast('다운로드할 데이터가 없습니다.', 'warning');
        return;
      }

      // 엑셀 데이터 형식으로 변환
      const excelData = filteredRequests.map(req => ({
        '신청자': req.employee?.name || '알 수 없음',
        '휴가 구분': req.type,
        '시작일': new Date(req.startDate).toLocaleDateString('ko-KR'),
        '종료일': new Date(req.endDate).toLocaleDateString('ko-KR'),
        '일수': req.days,
        '사유': req.reason,
        '승인자': req.approver || '-',
        '신청일': new Date(req.requestDate).toLocaleDateString('ko-KR')
      }));

      // 워크시트 생성
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // 컬럼 너비 설정
      const columnWidths = [
        { wch: 10 },  // 신청자
        { wch: 12 },  // 휴가 구분
        { wch: 15 },  // 시작일
        { wch: 15 },  // 종료일
        { wch: 8 },   // 일수
        { wch: 30 },  // 사유
        { wch: 10 },  // 승인자
        { wch: 15 }   // 신청일
      ];
      worksheet['!cols'] = columnWidths;

      // 워크북 생성
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '승인된 휴가 신청');

      // 파일명 생성 (연도 포함)
      const fileName = selectedYear === 'all'
        ? `승인된_휴가_신청_전체.xlsx`
        : `승인된_휴가_신청_${selectedYear}년.xlsx`;

      // 다운로드
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      showToast('엑셀 다운로드 중 오류가 발생했습니다.', 'error');
    }
  };

  return (
    <div className="p-6 lg:p-8">
      <header className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">근태 관리</h1>
          <p className="text-slate-500 mt-1">전 직원의 근태 신청을 관리하고 현황을 확인합니다.</p>
        </div>
      </header>

      {/* 결재 대기 목록 */}
      <section className="mb-8">
        <h2 className="text-xl font-bold text-slate-700 mb-4">
          결재 대기 목록 ({pendingRequests.length})
        </h2>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                <tr>
                  <th className="px-6 py-3 font-medium text-left">신청자</th>
                  <th className="px-6 py-3 font-medium text-left">구분</th>
                  <th className="px-6 py-3 font-medium text-left">기간</th>
                  <th className="px-6 py-3 font-medium text-left">사유</th>
                  <th className="px-6 py-3 font-medium text-center">관리</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                {pendingRequests.length === 0 ? (
                  <tr className="border-t border-slate-200">
                    <td colSpan={5} className="px-6 py-10 text-center text-slate-400">
                      결재 대기 중인 항목이 없습니다.
                    </td>
                  </tr>
                ) : (
                  pendingRequests.map((request) => (
                    <tr key={`${request.requestType}-${request.id}`} className="border-t border-slate-200">
                      <td className="px-6 py-4 font-semibold">{request.applicant}</td>
                      <td className="px-6 py-4">
                        {request.requestType === 'cancel' ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            {request.type}
                          </span>
                        ) : (
                          request.type
                        )}
                      </td>
                      <td className="px-6 py-4">{request.period}</td>
                      <td className="px-6 py-4">{request.reason}</td>
                      <td className="px-6 py-4 text-center space-x-2">
                        <button 
                          onClick={() => openConfirmationModal(request, true)}
                          className="px-3 py-1 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                        >
                          승인
                        </button>
                        <button 
                          onClick={() => openConfirmationModal(request, false)}
                          className="px-3 py-1 text-xs font-medium text-white bg-rose-600 rounded-md hover:bg-rose-700"
                        >
                          반려
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      
      {/* 승인된 요청 */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <h2 className="text-xl font-bold text-slate-800">승인된 요청</h2>
            <span className="ml-3 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
              {approvedRequests.length}건
            </span>
          </div>
          <div className="flex items-center space-x-3">
            {filteredByYear.length > 0 && (
              <span className="text-sm text-slate-500">
                {startIndex + 1}-{Math.min(endIndex, filteredByYear.length)} / {filteredByYear.length}
              </span>
            )}
            <button
              onClick={handleExportToExcel}
              disabled={approvedRequests.length === 0}
              className="px-4 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>엑셀 다운로드</span>
            </button>
          </div>
        </div>

        {/* 연도 필터 */}
        {approvedRequests.length > 0 && (
          <div className="mb-4 flex items-center space-x-2">
            <span className="text-sm font-medium text-slate-600">연도:</span>
            <button
              onClick={() => handleYearChange('all')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                selectedYear === 'all'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
              }`}
            >
              전체
            </button>
            {availableYears.map((year) => (
              <button
                key={year}
                onClick={() => handleYearChange(year.toString())}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  selectedYear === year.toString()
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
                }`}
              >
                {year}
              </button>
            ))}
          </div>
        )}

        {approvedRequests.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-8">
            <div className="text-center text-slate-500">
              <svg className="w-12 h-12 mx-auto mb-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm">승인된 요청이 없습니다.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 font-medium text-left">신청자</th>
                      <th className="px-6 py-3 font-medium text-left">구분</th>
                      <th className="px-6 py-3 font-medium text-left">기간</th>
                      <th className="px-6 py-3 font-medium text-left">사유</th>
                      <th className="px-6 py-3 font-medium text-center">관리</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-700">
                    {currentApprovedRequests.map((request) => (
                      <tr key={request.id} className="border-t border-slate-200 hover:bg-slate-50">
                        <td className="px-6 py-4 font-semibold">{request.applicant}</td>
                        <td className="px-6 py-4">{request.type}</td>
                        <td className="px-6 py-4">{request.period}</td>
                        <td className="px-6 py-4">{request.reason}</td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => handleCancelApproval(request.id)}
                            className="px-3 py-1 text-xs font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100"
                          >
                            승인 취소
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center mt-4 space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  이전
                </button>

                <div className="flex space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    // 페이지가 많을 때 일부만 표시
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 2 && page <= currentPage + 2)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            currentPage === page
                              ? 'bg-indigo-600 text-white'
                              : 'text-slate-700 bg-white border border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (
                      page === currentPage - 3 ||
                      page === currentPage + 3
                    ) {
                      return (
                        <span key={page} className="px-2 py-2 text-slate-400">
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  다음
                </button>
              </div>
            )}
          </>
        )}
      </section>
      
      {/* 직원별 연차 현황 */}
      <section>
        <h2 className="text-xl font-bold text-slate-700 mb-4">직원별 연차 현황</h2>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                <tr>
                  <th className="px-6 py-3 font-medium text-left">이름</th>
                  <th className="px-6 py-3 font-medium text-left">연차 기준일</th>
                  <th className="px-6 py-3 font-medium text-right">발생 연차</th>
                  <th className="px-6 py-3 font-medium text-right">사용 연차</th>
                  <th className="px-6 py-3 font-medium text-right">잔여 연차</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                {leaveBalances.length === 0 ? (
                  <tr className="border-t border-slate-200">
                    <td colSpan={5} className="px-6 py-8 text-center text-slate-500">
                      연차 현황 데이터가 없습니다.
                    </td>
                  </tr>
                ) : (
                  leaveBalances.map((balance) => (
                    <tr key={balance.id} className="border-t border-slate-200">
                      <td className="px-6 py-4 font-semibold">{balance.name}</td>
                      <td className="px-6 py-4">{new Date(balance.joinDate).toLocaleDateString('ko-KR')}</td>
                      <td className="px-6 py-4 text-right">{balance.totalLeave.toFixed(1)}</td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleUsedLeaveClick(balance.id, balance.name)}
                          className="text-indigo-600 hover:text-indigo-800 hover:underline font-semibold cursor-pointer"
                        >
                          {balance.usedLeave.toFixed(1)}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-indigo-600">
                        {balance.remainingLeave.toFixed(1)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <ConfirmationModal
        isOpen={isModalOpen}
        request={selectedRequest}
        isApproval={isApproval}
        onClose={closeConfirmationModal}
        onConfirm={handleSubmitApproval}
      />

      <UsedLeaveModal
        isOpen={isUsedLeaveModalOpen}
        employeeName={selectedEmployeeName}
        usedLeaves={selectedEmployeeUsedLeaves}
        onClose={closeUsedLeaveModal}
      />
    </div>
  );
};

export default AttendanceAdmin;